<template>
  <div
    class="slide-captcha"
    :class="['is-' + theme, 'is-' + status]"
    role="group"
    aria-label="滑动拼图验证"
  >
    <div ref="stage" class="slide-captcha__stage">
      <img v-if="background" :src="background" class="slide-captcha__bg" alt="" draggable="false">
      <img
        v-if="slider"
        :src="slider"
        class="slide-captcha__piece"
        :class="{ 'is-moving': dragging }"
        :style="pieceStyle"
        alt="拼图块"
        draggable="false"
      >
      <div v-if="loading && !uuid" class="slide-captcha__mask">正在加载验证码</div>
      <div v-else-if="status === 'ok'" class="slide-captcha__mask is-ok">
        <span class="slide-captcha__check">✓</span>
        验证成功
      </div>
      <button
        v-if="status !== 'ok'"
        type="button"
        class="slide-captcha__refresh"
        title="刷新验证码"
        :disabled="loading"
        @click.stop="refresh"
      >刷新验证码</button>
    </div>
    <div
      class="slide-captcha__bar"
      @mousedown.prevent="start"
      @touchstart.prevent="start"
    >
      <div class="slide-captcha__track" :style="{ width: barFill + 'px' }"></div>
      <button
        type="button"
        class="slide-captcha__knob"
        :style="{ transform: 'translateX(' + offset + 'px)' }"
        :disabled="status === 'ok' || verifying"
        aria-label="向右拖动滑块填充拼图"
      >
        <span v-if="status === 'ok'">✓</span>
        <span v-else>→</span>
      </button>
      <span class="slide-captcha__hint">{{ hint }}</span>
    </div>
  </div>
</template>

<script>
const HINT = '向右拖动滑块填充拼图'

export default {
  name: 'SlideCaptcha',
  props: {
    request: { type: Function, required: true },
    theme: { type: String, default: 'light' }
  },
  data() {
    return {
      uuid: '',
      background: '',
      slider: '',
      height: 400,
      width: 700,
      displayWidth: 350,
      displayHeight: 200,
      piece: 96,
      offset: 0,
      tilt: 0,
      dragging: false,
      loading: false,
      verifying: false,
      status: 'idle',
      hint: HINT,
      startX: 0,
      startY: 0,
      lastX: 0,
      startOffset: 0,
      trail: [],
      startedAt: 0,
      refreshTimer: null
    }
  },
  computed: {
    pieceStyle() {
      return {
        transform: 'translate3d(' + this.offset + 'px,0,0) rotate(' + this.tilt + 'deg)'
      }
    },
    barFill() {
      return Math.max(this.offset + 20, 0)
    }
  },
  created() {
    this.refresh()
  },
  beforeDestroy() {
    this.unbind()
    this.clearRefreshTimer()
  },
  methods: {
    ratio() {
      const el = this.$refs.stage
      return el && this.width ? el.clientWidth / this.width : 1
    },
    toImageX(px) {
      return Math.round(px / this.ratio())
    },
    maxOffset() {
      const el = this.$refs.stage
      const width = el ? el.clientWidth : this.width
      return Math.max(0, width - this.piece * this.ratio())
    },
    pointer(event) {
      if (event.touches && event.touches[0]) {
        return { x: event.touches[0].clientX, y: event.touches[0].clientY, trusted: event.isTrusted !== false }
      }
      return { x: event.clientX, y: event.clientY, trusted: event.isTrusted !== false }
    },
    pushTrail(x, y, trusted) {
      const point = [Date.now() - this.startedAt, x, y, trusted ? 1 : 0]
      const last = this.trail[this.trail.length - 1]
      if (!last || last[1] !== point[1] || last[2] !== point[2] || point[0] - last[0] > 16) {
        if (this.trail.length < 100) this.trail.push(point)
        else this.trail[this.trail.length - 1] = point
      }
    },
    clearRefreshTimer() {
      if (this.refreshTimer) {
        window.clearTimeout(this.refreshTimer)
        this.refreshTimer = null
      }
    },
    async refresh() {
      this.clearRefreshTimer()
      this.loading = true
      this.offset = 0
      this.tilt = 0
      this.status = 'idle'
      this.hint = HINT
      this.trail = []
      this.uuid = ''
      this.$emit('ticket', '')
      try {
        const res = await this.request({ url: '/captcha/slide', method: 'GET' })
        const data = (res && (res.data || res)) || {}
        this.uuid = data.uuid
        this.background = data.background
        this.slider = data.slider
        this.height = data.height || 400
        this.width = data.width || 700
        this.displayWidth = data.displayWidth || 350
        this.displayHeight = data.displayHeight || 200
        this.piece = data.piece || 96
      } catch (e) {
        this.hint = (e && e.message) || '验证码加载失败，点击刷新重试'
        this.status = 'fail'
      } finally {
        this.loading = false
      }
    },
    start(event) {
      if (this.status === 'ok' || this.verifying) return
      if (this.loading || !this.uuid) {
        this.refresh()
        return
      }
      const p = this.pointer(event)
      this.dragging = true
      this.status = 'drag'
      this.hint = '松开鼠标完成拼合'
      this.startX = p.x
      this.startY = p.y
      this.lastX = p.x
      this.startOffset = this.offset
      this.startedAt = Date.now()
      this.trail = [[0, this.toImageX(this.offset), 0, p.trusted ? 1 : 0]]
      window.addEventListener('mousemove', this.move)
      window.addEventListener('mouseup', this.end)
      window.addEventListener('touchmove', this.move, { passive: false })
      window.addEventListener('touchend', this.end)
    },
    move(event) {
      if (!this.dragging) return
      if (event.cancelable) event.preventDefault()
      const p = this.pointer(event)
      this.offset = Math.min(this.maxOffset(), Math.max(0, this.startOffset + p.x - this.startX))
      const vx = p.x - this.lastX
      this.lastX = p.x
      this.tilt = Math.max(-14, Math.min(14, vx * 0.9))
      this.pushTrail(this.toImageX(this.offset), Math.round(p.y - this.startY), p.trusted)
    },
    async end() {
      if (!this.dragging) return
      this.dragging = false
      this.tilt = 0
      this.unbind()
      const x = this.toImageX(this.offset)
      if (x < 12) {
        this.offset = 0
        this.status = 'idle'
        this.hint = HINT
        return
      }
      this.verifying = true
      this.hint = '正在校验...'
      try {
        const duration = Date.now() - this.startedAt
        this.pushTrail(x, this.trail.length ? this.trail[this.trail.length - 1][2] : 0, true)
        const res = await this.request({
          url: '/captcha/slide/verify',
          method: 'POST',
          body: { uuid: this.uuid, x, duration, trail: this.trail }
        })
        const ticket = res && (res.ticket || (res.data && res.data.ticket))
        if (!ticket) {
          throw new Error((res && res.msg) || '请控制拼图对齐缺口')
        }
        this.status = 'ok'
        this.hint = '验证成功'
        this.$emit('ticket', ticket)
      } catch (e) {
        this.status = 'fail'
        this.hint = (e && e.message) || '请控制拼图对齐缺口'
        this.$emit('ticket', '')
        this.clearRefreshTimer()
        this.refreshTimer = window.setTimeout(() => {
          this.refreshTimer = null
          this.offset = 0
          this.refresh()
        }, 480)
      } finally {
        this.verifying = false
      }
    },
    unbind() {
      window.removeEventListener('mousemove', this.move)
      window.removeEventListener('mouseup', this.end)
      window.removeEventListener('touchmove', this.move)
      window.removeEventListener('touchend', this.end)
    }
  }
}
</script>

<style scoped>
.slide-captcha { width: min(350px, 100%); max-width: 100%; }
.slide-captcha__stage {
  position: relative;
  width: 100%;
  aspect-ratio: 350 / 200;
  overflow: hidden;
  border-radius: 4px;
  background: #16384c;
}
.slide-captcha__bg,
.slide-captcha__piece {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  user-select: none;
  pointer-events: none;
}
.slide-captcha__bg { width: 100%; height: 100%; object-fit: fill; }
.slide-captcha__piece {
  width: auto;
  transform-origin: 30% 50%;
  filter: drop-shadow(1px 2px 3px rgba(0,0,0,.35));
}
.slide-captcha__piece.is-moving { transition: none; }
.slide-captcha__mask {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  background: rgba(8, 20, 28, .38);
}
.slide-captcha__mask.is-ok { background: rgba(25, 155, 93, .72); }
.slide-captcha__check {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #fff;
  color: #15803d;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}
.slide-captcha__refresh {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
  border: 0;
  border-radius: 3px;
  padding: 2px 8px;
  background: rgba(0,0,0,.45);
  color: #fff;
  font-size: 12px;
  cursor: pointer;
}
.slide-captcha__refresh:disabled {
  cursor: wait;
  opacity: .65;
}
.slide-captcha__bar {
  position: relative;
  margin-top: 8px;
  height: 40px;
  border-radius: 4px;
  background: #f7f9fc;
  box-shadow: inset 0 0 0 1px #e5eaf0;
  cursor: pointer;
  overflow: hidden;
}
.slide-captcha__track {
  height: 100%;
  background: #d8ecff;
}
.slide-captcha__knob {
  position: absolute;
  top: 4px;
  left: 4px;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 3px;
  background: #fff;
  color: #2f6fed;
  font-size: 16px;
  font-weight: 700;
  cursor: grab;
  box-shadow: 0 1px 4px rgba(15, 40, 60, .18);
}
.slide-captcha__hint {
  position: absolute;
  inset: 0 8px 0 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: #8b93a0;
  pointer-events: none;
}
.is-ok .slide-captcha__track { background: #b7f0c8; }
.is-ok .slide-captcha__knob { background: #19b34c; color: #fff; cursor: default; }
.is-ok .slide-captcha__hint { color: #15803d; font-weight: 700; }
.is-fail .slide-captcha__bar { animation: captcha-shake .35s; }
.is-fail .slide-captcha__hint { color: #c0392b; }
.is-dark .slide-captcha__bar {
  background: rgba(255,255,255,.08);
  box-shadow: inset 0 0 0 1px rgba(255,255,255,.16);
}
.is-dark .slide-captcha__track { background: rgba(197, 160, 89, .28); }
.is-dark .slide-captcha__hint { color: rgba(230,224,214,.78); }
.is-dark .slide-captcha__knob { background: #c5a059; color: #2b2110; }
.is-dark.is-ok .slide-captcha__hint { color: #b7f7c8; }
@keyframes captcha-shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-4px); }
  75% { transform: translateX(4px); }
}
</style>
